import React from 'react';
import { ArrowDownRight, ArrowUpRight, Minus, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtAbsChange, fmtChange, fmtNumber } from '@/domain/numbers';
import { metricText, UNAVAILABLE_SHORT } from '@/lib/labels';
import { Sparkline } from './Charts';

/**
 * Endring mot referanse som pille: tekst + ikon, aldri bare farge.
 * Andeler (isShare) vises KUN i prosentpoeng; øvrige i relativ prosent med absolutt endring i parentes.
 */
export function DeltaBadge({ row, invertGood = false, className, compact = false }) {
  if (!row) return null;
  if (row.status === 'unavailable') return <span className={cn('text-[13px] text-[var(--hr-muted)]', className)}>Sammenligning utilgjengelig</span>;
  if (row.status === 'cannot') return <span className={cn('text-[13px] text-[var(--hr-muted)]', className)}>Kan ikke beregnes (referanse 0)</span>;
  const v = row.isShare ? row.ppChange : row.pctChange;
  if (v === null || v === undefined) return <span className={cn('text-[13px] text-[var(--hr-muted)]', className)}>–</span>;
  const neutral = Math.abs(v) < 0.05;
  const worse = invertGood ? v < 0 : v > 0;
  const Icon = neutral ? Minus : v > 0 ? ArrowUpRight : ArrowDownRight;
  const tone = neutral ? 'bg-[var(--hr-inset)] text-[var(--hr-muted)]' : worse ? 'bg-[var(--hr-red-bg)] text-[var(--hr-red)]' : 'bg-[var(--hr-green-bg)] text-[var(--hr-green)]';
  const secondary = row.isShare ? `fra ${fmtNumber(row.reference?.value, row.decimals)} %` : `(${fmtAbsChange(row)})`;
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-x-2 gap-y-1', className)}>
      <span className={cn('hr-tabular inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[13px] font-semibold', tone)} data-testid={row.key ? `delta-${row.key}` : undefined}>
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {fmtChange(row, 1)}
      </span>
      {!compact && <span className="hr-tabular text-[13px] text-[var(--hr-muted)]">{secondary}</span>}
    </span>
  );
}

/** Verdi + enhet i stor numerisk typografi. Utilgjengelig vises som tekst, aldri som 0. */
function BigValue({ metric, testKey, size = 'md' }) {
  const unavailable = metric.status === 'unavailable' || metric.value === null;
  if (unavailable) {
    return (
      <div className="flex flex-col">
        <span data-testid={`kpi-${testKey}-value`} className={cn('font-semibold leading-tight text-[var(--hr-amber)]', size === 'lg' ? 'text-[20px]' : 'text-[18px]')}>
          {UNAVAILABLE_SHORT[metric.reason] ?? 'Utilgjengelig'}
        </span>
        <span className="mt-1 text-[13px] leading-5 text-[var(--hr-muted)]">{metric.message}</span>
        {metric.numerator?.partial && (
          <span className="hr-tabular mt-1 text-[13px] text-[var(--hr-muted)]">
            Registrert, delvis: {fmtNumber(metric.numerator.value, 1)} {metric.numerator.unit}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-baseline gap-1.5">
      <span data-testid={`kpi-${testKey}-value`} className={cn('hr-num font-semibold leading-none text-[var(--hr-heading)]', size === 'lg' ? 'text-[28px] sm:text-[32px]' : 'text-[26px] sm:text-[28px]')}>
        {fmtNumber(metric.value, metric.decimals)}
      </span>
      <span className="text-sm font-medium text-[var(--hr-muted)]">{metric.unit}</span>
    </div>
  );
}

function Illustrative() {
  return <span className="ml-1.5 rounded-full bg-[var(--hr-amber-bg)] px-1.5 py-px text-[12px] font-medium text-[var(--hr-amber)]">illustrativ</span>;
}

/** Diskret hint som bare vises ved hover/fokus – forteller at flisen kan åpnes. */
function ExplainHint() {
  return (
    <span className="-mr-1 -mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[var(--hr-muted-2)] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden="true">
      <Info className="h-4 w-4" />
    </span>
  );
}

/**
 * KPI-flis (frittstående flate). Hele flisen åpner forklaringen.
 */
export function KpiCell({ metric, row, invertGood, onExplain, testKey, hidden, hiddenText, spark, sparkLabel }) {
  if (hidden) {
    return (
      <div className="hr-surface flex min-w-0 flex-col gap-1 px-5 py-4" data-testid={`kpi-${testKey}-hidden`}>
        <span className="text-[14px] font-medium text-[var(--hr-muted)]">{metric?.label}</span>
        <span className="mt-1 text-sm text-[var(--hr-muted)]">{hiddenText ?? 'Skjult for valgt rolle'}</span>
      </div>
    );
  }
  const unavailable = metric.status === 'unavailable' || metric.value === null;
  const Tag = onExplain ? 'button' : 'div';
  return (
    <Tag
      type={onExplain ? 'button' : undefined}
      onClick={onExplain}
      data-testid={onExplain ? `kpi-${testKey}-explain-button` : `kpi-${testKey}`}
      aria-label={onExplain ? `Forklar beregning: ${metric.label}` : undefined}
      className={cn(
        'hr-surface group relative flex min-w-0 flex-col gap-2.5 px-5 py-4 text-left transition-[background-color,border-color] duration-150',
        onExplain && 'cursor-pointer hover:border-[rgba(31,33,48,0.18)] hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--hr-bg)]',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-[14px] font-medium leading-5 text-[var(--hr-muted)]">
          {metric.label}
          {metric.isIllustrative && <Illustrative />}
        </span>
        {onExplain && <ExplainHint />}
      </div>
      <div className="flex items-end justify-between gap-3">
        <BigValue metric={metric} testKey={testKey} />
        {!unavailable && spark && spark.some((v) => v !== null && v !== undefined) && <Sparkline values={spark} label={sparkLabel} />}
      </div>
      <div className="flex min-h-[20px] flex-wrap items-center gap-x-3 gap-y-1">
        {!unavailable && row && <DeltaBadge row={row} invertGood={invertGood} />}
        {metric.status === 'partial' && <span className="text-[13px] font-medium text-[var(--hr-amber)]">Registrert, delvis</span>}
      </div>
    </Tag>
  );
}

/** Rutenett av KPI-fliser (hver flis er én flate – ingen kort i kort). */
export function KpiRow({ children, columns = 4, className, ...props }) {
  const cols = columns === 2 ? 'lg:grid-cols-2' : columns === 3 ? 'lg:grid-cols-3' : columns === 6 ? 'lg:grid-cols-3 xl:grid-cols-6' : 'xl:grid-cols-4';
  return (
    <div className={cn('grid grid-cols-1 gap-3 xs:grid-cols-2 sm:gap-4', cols, className)} {...props}>
      {children}
    </div>
  );
}

/**
 * KPI-stripe: ÉN rolig flate delt i celler med hårlinjer – de få viktigste tallene,
 * i stedet for mange like kort. Hver celle kan åpne forklaringen.
 */
export function KpiStrip({ children, columns = 4, className, ...props }) {
  const cols = columns === 3 ? 'lg:grid-cols-3' : columns === 2 ? 'sm:grid-cols-2' : 'xl:grid-cols-4';
  return (
    <div className={cn('hr-surface grid grid-cols-1 overflow-hidden sm:grid-cols-2', cols, className)} {...props}>
      {children}
    </div>
  );
}

const STRIP_CELL = 'relative flex min-w-0 flex-col gap-2.5 px-5 py-4 text-left border-[var(--hr-hairline)] border-t sm:[&:nth-child(-n+2)]:border-t-0 lg:[&:nth-child(-n+4)]:border-t-0 sm:[&:nth-child(2n)]:border-l lg:[&:not(:first-child)]:border-l';

export function KpiStripCell({ metric, row, invertGood, onExplain, testKey, hidden, hiddenText, spark, sparkLabel, sparkColor }) {
  const base = STRIP_CELL;
  if (hidden) {
    return (
      <div className={cn(base, 'justify-center')} data-testid={`kpi-${testKey}-hidden`}>
        <span className="text-[14px] font-medium text-[var(--hr-muted)]">{metric?.label}</span>
        <span className="text-sm leading-6 text-[var(--hr-muted)]">{hiddenText ?? 'Skjult for valgt rolle'}</span>
      </div>
    );
  }
  const unavailable = metric.status === 'unavailable' || metric.value === null;
  const Tag = onExplain ? 'button' : 'div';
  return (
    <Tag
      type={onExplain ? 'button' : undefined}
      onClick={onExplain}
      data-testid={onExplain ? `kpi-${testKey}-explain-button` : `kpi-${testKey}`}
      aria-label={onExplain ? `Forklar beregning: ${metric.label}` : undefined}
      className={cn(base, 'group transition-[background-color] duration-150', onExplain && 'cursor-pointer hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]')}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-[14px] font-medium leading-5 text-[var(--hr-muted)]">
          {metric.label}
          {metric.isIllustrative && <Illustrative />}
        </span>
        {onExplain && <ExplainHint />}
      </div>
      <div className="flex items-end justify-between gap-3">
        <BigValue metric={metric} testKey={testKey} size="lg" />
        {!unavailable && spark && spark.some((v) => v !== null && v !== undefined) && <Sparkline values={spark} label={sparkLabel} color={sparkColor} width={104} height={32} className="hidden xs:block" />}
      </div>
      <div className="flex min-h-[20px] flex-wrap items-center gap-x-3 gap-y-1">
        {!unavailable && row && <DeltaBadge row={row} invertGood={invertGood} />}
        {metric.status === 'partial' && <span className="text-[13px] font-medium text-[var(--hr-amber)]">Registrert, delvis</span>}
      </div>
    </Tag>
  );
}

/**
 * Sekundært måltall i én linje (etikett · verdi · endring). Brukes under KPI-stripen
 * for tall som er nyttige, men ikke skal konkurrere om oppmerksomheten.
 */
export function InlineMetric({ metric, row, invertGood, onExplain, testKey, hidden, hiddenText, note }) {
  if (hidden) {
    return (
      <div className="flex h-full min-w-0 flex-col gap-1.5 bg-white px-5 py-4" data-testid={`kpi-${testKey}-hidden`}>
        <span className="text-[13px] font-medium text-[var(--hr-muted)]">{metric?.label}</span>
        <span className="text-sm text-[var(--hr-muted)]">{hiddenText ?? 'Skjult for valgt rolle'}</span>
      </div>
    );
  }
  const unavailable = metric.status === 'unavailable' || metric.value === null;
  const Tag = onExplain ? 'button' : 'div';
  return (
    <Tag
      type={onExplain ? 'button' : undefined}
      onClick={onExplain}
      data-testid={onExplain ? `kpi-${testKey}-explain-button` : `kpi-${testKey}`}
      aria-label={onExplain ? `Forklar beregning: ${metric.label}` : undefined}
      className={cn('group flex h-full min-w-0 flex-col gap-1.5 bg-white px-5 py-4 text-left', onExplain && 'cursor-pointer transition-[background-color] duration-150 hover:bg-[var(--hr-panel)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]')}
    >
      <span className="flex flex-wrap items-center gap-x-1.5 text-[13px] font-medium leading-4 text-[var(--hr-muted)]">
        <span>{metric.label}</span>
        {metric.isIllustrative && <Illustrative />}
      </span>
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {unavailable ? (
          <span className="text-sm font-semibold text-[var(--hr-amber)]" data-testid={`kpi-${testKey}-value`}>{UNAVAILABLE_SHORT[metric.reason] ?? 'Utilgjengelig'}</span>
        ) : (
          <>
            <span className="hr-num text-[22px] font-semibold leading-none text-[var(--hr-heading)]" data-testid={`kpi-${testKey}-value`}>{fmtNumber(metric.value, metric.decimals)}</span>
            <span className="text-[13px] font-medium text-[var(--hr-muted)]">{metric.unit}</span>
            {row && <DeltaBadge row={row} invertGood={invertGood} compact />}
          </>
        )}
      </span>
      {note && <span className="mt-auto text-[12px] leading-4 text-[var(--hr-muted-2)]">{note}</span>}
    </Tag>
  );
}

/** Generisk celle i en KpiStrip for ferdig formaterte verdier (maskindetalj, tabellsummer). */
export function StripCell({ label, value, unit, meta, metaTone, onClick, testId, valueTestId, spark, sparkColor, sparkLabel }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      className={cn(STRIP_CELL, 'group transition-[background-color] duration-150', onClick && 'cursor-pointer hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]')}
    >
      <span className="text-[14px] font-medium leading-5 text-[var(--hr-muted)]">{label}</span>
      <span className="flex items-end justify-between gap-3">
        <span className="flex items-baseline gap-1.5">
          <span className="hr-num text-[28px] font-semibold leading-none text-[var(--hr-heading)] sm:text-[32px]" data-testid={valueTestId}>
            {value}
          </span>
          {unit && <span className="text-sm font-medium text-[var(--hr-muted)]">{unit}</span>}
        </span>
        {spark && spark.some((v) => typeof v === 'number') && <Sparkline values={spark} color={sparkColor} label={sparkLabel} width={104} height={32} className="hidden xs:block" />}
      </span>
      {meta && <span className={cn('text-[13px] leading-5', metaTone === 'amber' ? 'font-medium text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>{meta}</span>}
    </Tag>
  );
}

/**
 * Lav sammendragsrad: fire små tall på én rolig linje (2 × 2 på mobil, 4 i bredden fra md).
 * Brukes der KPI-stripen blir for høy, f.eks. i Avvik-oversikten på nettbrett/mellomstore skjermer.
 */
export function SummaryRow({ children, className, ...props }) {
  return (
    <div className={cn('hr-surface grid grid-cols-2 overflow-hidden md:grid-cols-4', className)} {...props}>
      {children}
    </div>
  );
}

const SUMMARY_CELL = 'flex min-w-0 flex-col gap-0.5 px-4 py-2.5 text-left border-[var(--hr-hairline)] [&:nth-child(n+3)]:border-t md:[&:nth-child(n+3)]:border-t-0 [&:nth-child(2n)]:border-l md:[&:not(:first-child)]:border-l';

export function SummaryCell({ label, value, meta, metaTone, onClick, testId, valueTestId }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} onClick={onClick} data-testid={testId} className={cn(SUMMARY_CELL, onClick && 'cursor-pointer transition-[background-color] duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]')}>
      <span className="truncate text-[13px] font-medium leading-4 text-[var(--hr-muted)]">{label}</span>
      <span className="hr-num text-[20px] font-semibold leading-6 text-[var(--hr-heading)]" data-testid={valueTestId}>{value}</span>
      {meta && <span className={cn('truncate text-[12px] leading-4', metaTone === 'amber' ? 'font-medium text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>{meta}</span>}
    </Tag>
  );
}

/** Enkel «råflis» for maskindetalj o.l. */
export function StatTile({ label, value, unit, meta, metaTone, onClick, testId, valueTestId, className, spark, sparkColor }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      className={cn('hr-surface flex min-w-0 flex-col gap-2 px-5 py-4 text-left', onClick && 'group cursor-pointer transition-[background-color] duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--hr-bg)]', className)}
    >
      <span className="text-[14px] font-medium leading-5 text-[var(--hr-muted)]">{label}</span>
      <span className="flex items-end justify-between gap-3">
        <span className="flex items-baseline gap-1.5">
          <span className="hr-num text-[28px] font-semibold leading-none text-[var(--hr-heading)]" data-testid={valueTestId}>
            {value}
          </span>
          {unit && <span className="text-sm font-medium text-[var(--hr-muted)]">{unit}</span>}
        </span>
        {spark && spark.some((v) => typeof v === 'number') && <Sparkline values={spark} color={sparkColor} />}
      </span>
      {meta && <span className={cn('text-[13px] leading-5', metaTone === 'amber' ? 'font-medium text-[var(--hr-amber)]' : 'text-[var(--hr-muted-2)]')}>{meta}</span>}
    </Tag>
  );
}

export function MetricInline({ metric, className }) {
  return <span className={cn('hr-tabular', metric?.status === 'unavailable' ? 'text-[var(--hr-amber)]' : '', className)}>{metricText(metric)}</span>;
}
